/**
 * Created by Jamsheed on 13/07/16.
 */
// @flow

import _ from 'lodash';

import React from 'react';
import VisitUtils from 'common/utils/VisitUtils';
import Units from 'common/enums/UnitEnum';
import type {
    TaskEntitySettings,
    CustomField
} from 'common/models/internal/EntitySettings';

import { VisitColumnType } from 'common/enums/VisitEnums';

import I18nHelper from 'common/helpers/I18nHelper';
import type { Map, List } from 'immutable';

const translationsNamespace = ['liveView'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

const TaskTypes = {
    AllTasks: 'ALL TASKS',
    Ongoing: 'ONGOING',
    Completed: 'COMPLETED',
    Cancelled: 'CANCELLED',
    Waiting: 'WAITING'
};

export type VisitTableColumnsType = {
    title: string,
    sortKey?: string | Array<string>,
    plainTextKey?: string,
    width: number,
    fixed?: boolean,
    visible: boolean,
    visibleOn?: Array<string>,
    key?: string,
    index?: number,
    columnType?: string,
    disabled?: boolean,
    isDraggable?: boolean
};

class VisitTableUtils {
    static staticResources: Array<string> = ['VOLUME', 'WEIGHT'];

    static getTaskVisitModel: Function = (
        unitConverter,
        taskSettings: ?TaskEntitySettings,
        t: (string, ?Object) => string,
        taskVisits: List<Map<string, any>>
    ) => {
        const distanceUnit = unitConverter.getConvertedUnitDisplayText({
            sourceUnit: Units.list.length.Meter
        });
        const columns = {
            taskIdElement: {
                title: t('translations:taskId'),
                sortKey: ['sourceId'],
                plainTextKey: 'taskId',
                width: 100,
                fixed: true,
                visible: true
            },
            sourceOrderId: {
                title: t('translations:alternateId'),
                sortKey: ['sourceOrderId'],
                width: 100,
                visible: false
            },
            batchId: {
                title: t('translations:batchId'),
                sortKey: ['batchId'],
                width: 100,
                visible: false
            },
            tourId: {
                title: t('tourId'),
                sortKey: ['tourId', 'tourId'],
                width: 100,
                visible: false
            },
            visitId: {
                title: t('visitId'),
                sortKey: ['id'],
                width: 110,
                visible: false
            },
            visitType: {
                title: t('translations:visitType'),
                sortKey: ['visitType'],
                width: 90,
                visible: false
            },
            teamName: {
                title: t('teamName'),
                sortKey: ['team', 'name'],
                width: 90,
                visible: false
            },
            taskStatusElement: {
                title: t('taskStatus'),
                sortKey: ['taskStatus'],
                plainTextKey: 'taskStatus',
                width: 100,
                visible: true
            },
            statusElement: {
                title: t('visitStatusLabel'),
                sortKey: ['visitStatus'],
                plainTextKey: 'status',
                width: 100,
                visible: true
            },
            usernameLink: {
                title: t('entities:rider'),
                sortKey: ['assignedUser', 'name'],
                plainTextKey: 'username',
                width: 100,
                visible: true
            },
            userId: {
                title: t('riderId'),
                sortKey: ['assignedUser', 'userId'],
                plainTextKey: 'userId',
                width: 100,
                visible: false
            },
            locationIdElement: {
                title: t('locationId'),
                sortKey: ['locationId', 'locationId'],
                width: 90,
                visible: true,
                relatedExportColumns: [
                    { title: t('locationVersion'), visible: true }
                ]
            },
            locationName: {
                title: t('placeName'),
                sortKey: ['chosenLocation', 'address', 'placeName'],
                width: 90,
                visible: true
            },
            customer: {
                title: t('translations:customerName'),
                sortKey: ['chosenLocation', 'contact', 'name'],
                width: 130,
                visible: true
            },
            address: {
                title: t('customerAddress'),
                sortKey: ['chosenLocation', 'address', 'formattedAddress'],
                width: 200,
                flexGrow: 1,
                visible: true
            },
            date: {
                title: t('date'),
                sortKey: ['chosenLocation', 'slot', 'startTime'],
                width: 100,
                visible: false
            },
            slotStartElement: {
                title: t('slotStart'),
                width: 100,
                visible: true,
                sortKey: ['chosenLocation', 'slot', 'startTime'],
                plainTextKey: 'slotStartFormatted'
            },
            slotEndElement: {
                title: t('slotEnd'),
                width: 100,
                visible: true,
                sortKey: ['chosenLocation', 'slot', 'endTime'],
                plainTextKey: 'slotEndFormatted'
            },
            initialSlotStart: {
                title: t('initialSlotStart'),
                width: 100,
                visible: false,
                sortKey: ['initialSlot', 'start']
            },
            initialSlotEnd: {
                title: t('initialSlotEnd'),
                width: 100,
                visible: false,
                sortKey: ['initialSlot', 'end']
            },
            assignedTime: {
                title: t('assignedAt'),
                sortKey: ['statusUpdates', 'assigned', 'time'],
                width: 100,
                visible: false
            },
            acceptedTime: {
                title: t('acceptedAt'),
                sortKey: ['statusUpdates', 'accepted', 'time'],
                width: 100,
                visible: false,
                visibleOn: [
                    TaskTypes.Ongoing,
                    TaskTypes.Completed,
                    TaskTypes.Cancelled
                ]
            },
            startedTime: {
                title: t('startedAt'),
                sortKey: ['statusUpdates', 'started', 'time'],
                width: 83,
                visible: false,
                visibleOn: [
                    TaskTypes.Ongoing,
                    TaskTypes.Completed,
                    TaskTypes.Cancelled
                ]
            },
            initialEta: {
                title: t('initialEta'),
                sortKey: ['initialEta'],
                width: 100,
                visible: false
            },
            eta: {
                title: t('translations:currentEta'),
                sortKey: ['currentEta'],
                width: 83,
                visible: true
            },
            etaStatusElement: {
                title: t('slaStatus'),
                sortKey: ['slaStatus'],
                plainTextKey: 'slaStatus',
                width: 83,
                visible: true
            },
            arrivalTime: {
                title: t('reachedAt'),
                sortKey: ['statusUpdates', 'arrived', 'time'],
                width: 83,
                visible: false,
                visibleOn: [
                    TaskTypes.Ongoing,
                    TaskTypes.Completed,
                    TaskTypes.Cancelled
                ]
            },
            markedAcceptedBy: {
                title: t('acceptedBy'),
                sortKey: ['statusUpdates', 'accepted', 'actor'],
                width: 83,
                visible: false,
                visibleOn: [
                    TaskTypes.Ongoing,
                    TaskTypes.Completed,
                    TaskTypes.Cancelled
                ]
            },
            markedArrivedBy: {
                title: t('reachedBy'),
                sortKey: ['statusUpdates', 'arrived', 'actor'],
                width: 83,
                visible: false,
                visibleOn: [
                    TaskTypes.Ongoing,
                    TaskTypes.Completed,
                    TaskTypes.Cancelled
                ]
            },
            markedAssignedBy: {
                title: t('assignedBy'),
                sortKey: ['statusUpdates', 'assigned', 'actor'],
                width: 83,
                visible: false
            },
            markedCompletedBy: {
                title: t('completedBy'),
                sortKey: ['statusUpdates', 'completed', 'actor'],
                width: 83,
                visible: false,
                visibleOn: [
                    TaskTypes.Ongoing,
                    TaskTypes.Completed,
                    TaskTypes.Cancelled
                ]
            },
            markedStartedBy: {
                title: t('startedBy'),
                sortKey: ['statusUpdates', 'started', 'actor'],
                width: 83,
                visible: false,
                visibleOn: [
                    TaskTypes.Ongoing,
                    TaskTypes.Completed,
                    TaskTypes.Cancelled
                ]
            },
            arrivalDrift: {
                title: t('arrivalDrift', { unit: distanceUnit }),
                sortKey: ['statusUpdates', 'arrived', 'drift'],
                width: 83,
                visible: false,
                visibleOn: [
                    TaskTypes.Ongoing,
                    TaskTypes.Completed,
                    TaskTypes.Cancelled
                ]
            },
            initialEtd: {
                title: t('initialEtd'),
                sortKey: ['initialEtd'],
                width: 83,
                visible: false
            },
            completedTime: {
                title: t('completedAt'),
                sortKey: ['statusUpdates', 'completed', 'time'],
                width: 83,
                visible: true,
                visibleOn: [
                    TaskTypes.Ongoing,
                    TaskTypes.Completed,
                    TaskTypes.Cancelled
                ]
            },
            completedDrift: {
                title: t('completedDrift', { unit: distanceUnit }),
                sortKey: ['statusUpdates', 'completed', 'drift'],
                width: 83,
                visible: false,
                visibleOn: [
                    TaskTypes.Ongoing,
                    TaskTypes.Completed,
                    TaskTypes.Cancelled
                ]
            },
            previousAssignedRiderName: {
                title: t('initialRider'),
                sortKey: ['firstAssignedUser', 'name'],
                width: 100,
                visible: false,
                plainTextKey: 'previousAssignedRiderNameText'
            },
            plannedTransactionDuration: {
                title: t('translations:plannedTransactionTime'),
                sortKey: ['chosenLocation', 'slot', 'transactionDuration'],
                width: 83,
                visible: false
            },
            transactionDuration: {
                title: t('translations:transactionTime'),
                sortKey: ['transactionDuration'],
                width: 83,
                visible: false
            },
            volumeTransaction: {
                title: t('translations:volume'),
                sortKey: ['volume', 'value'],
                width: 83,
                visible: false
            },
            amountTransaction: {
                title: t('translations:amount'),
                sortKey: ['netAmount'],
                width: 83,
                visible: true
            },
            paymentCollected: {
                title: t('translations:amountCollected'),
                width: 90,
                visible: false
            },
            markedCancelledBy: {
                title: t('cancelledBy'),
                sortKey: ['statusUpdates', 'cancelled', 'actor'],
                width: 83,
                visible: false,
                visibleOn: [TaskTypes.Cancelled]
            },
            cancelledReason: {
                title: t('cancelledReason'),
                sortKey: ['cancelledReason'],
                width: 83,
                visible: false,
                visibleOn: [TaskTypes.Cancelled]
            },
            cancelledAt: {
                title: t('cancelledAt'),
                sortKey: ['cancelledAt'],
                width: 83,
                visible: false,
                visibleOn: [TaskTypes.Cancelled]
            },
            cancelledDrift: {
                title: t('cancelledDrift', { unit: distanceUnit }),
                sortKey: ['statusUpdates', 'cancelled', 'drift'],
                width: 83,
                visible: false,
                visibleOn: [
                    TaskTypes.Ongoing,
                    TaskTypes.Completed,
                    TaskTypes.Cancelled
                ]
            },
            podElement: {
                title: t('podElement'),
                width: 83,
                visible: false,
                visibleOn: [
                    TaskTypes.Ongoing,
                    TaskTypes.Completed,
                    TaskTypes.Cancelled
                ]
            },
            pocElement: {
                title: t('pocElement'),
                width: 83,
                visible: false,
                visibleOn: [TaskTypes.Cancelled]
            },
            appFieldsElement: {
                title: t('appFields'),
                width: 90,
                visible: false
            },
            lineItemsElement: {
                title: t('translations:lineItem_plural'),
                width: 90,
                sortKey: ['transactionStatus'],
                visible: false
            },
            transactionStatus: {
                title: t('transactionStatus'),
                sortKey: ['transactionStatus'],
                width: 90,
                visible: false
            },
            weight: {
                title: t('translations:weight'),
                sortKey: ['weight'],
                width: 90,
                visible: true,
                columnType: VisitColumnType.Resource
            },
            taskSource: { title: t('taskSource'), width: 100, visible: false }
        };
        const columnsWithCustomResources = taskVisits
            ? VisitTableUtils.getCustomResourceConfig(taskVisits, columns)
            : columns;
        if (taskSettings) {
            const { customFields } = taskSettings;
            return VisitTableUtils.getTaskCustomColumns(
                columnsWithCustomResources,
                customFields
            );
        }
        return columnsWithCustomResources;
    };

    static getCustomResourceConfig: Function = (
        taskVisits: List<Map<string, any>>,
        columns: { [string]: VisitTableColumnsType }
    ) => {
        const columnsWithResources = { ...columns };
        taskVisits.forEach(visit => {
            visit.get('resources').forEach(resource => {
                const resourceName = resource.get('name');
                if (
                    VisitTableUtils.staticResources.indexOf(resourceName) === -1
                ) {
                    columnsWithResources[resourceName] = {
                        title: resourceName,
                        width: 90,
                        visible: false,
                        columnType: VisitColumnType.Resource
                    };
                }
            });
        });
        return columnsWithResources;
    };

    static getTaskCustomColumns: Function = function (
        columns: Object,
        customFields: ?Array<CustomField>
    ) {
        const modifiedColumns = { ...columns };
        if (customFields) {
            customFields
                .filter(field => field.metadata.visible)
                .forEach(customField => {
                    const { metadata, name, key } = customField;
                    const { dataType } = metadata;
                    const newColumn = {
                        title: name,
                        plainTextKey: name,
                        sortKey: [key],
                        width: 100
                    };
                    if (dataType) {
                        modifiedColumns[key] = newColumn;
                    }
                });
        }

        return modifiedColumns;
    };

    static mergeModel(newModel, savedModel) {
        return _.mergeWith(
            _.cloneDeep(newModel),
            savedModel,
            VisitTableUtils.selectiveMerge
        );
    }

    static mergeModelOnExistingKeys(newModel, savedModel) {
        var mergedModel = _.cloneDeep(newModel);
        _.keys(savedModel).forEach(key => {
            if (mergedModel[key]) {
                mergedModel[key] = _.mergeWith(
                    mergedModel[key],
                    savedModel[key],
                    VisitTableUtils.selectiveMerge
                );
            }
        });
        return mergedModel;
    }

    static filterHomebaseVisits(taskVisits) {
        return taskVisits
            ? taskVisits.filter(taskVisit => {
                  return taskVisit.get('visitType') !== 'HOMEBASE';
              })
            : null;
    }

    static selectiveMerge(objValue, srcValue, key) {
        switch (key) {
            case 'width':
            case 'visible':
                return srcValue;
            default:
                return objValue;
        }
    }

    static formatDistance(distanceInMeters) {
        if (!distanceInMeters) {
            return '';
        }
        return (distanceInMeters / 1000).toFixed(2);
    }

    static getVisitTransactionStatus(visits) {
        return visits.withMutations(visits => {
            visits.forEach((visit, index) => {
                var status = VisitUtils.getTransactionStatusLabel({ visit });
                visits.setIn([index, 'transactionStatus'], status);
            });
        });
    }

    static mapVisitToColumnForCustomResources: Function = (
        visit: Map<string, any>,
        columnData: { [string]: string | number | React.ComponentType<any> }
    ) => {
        const columnDataWithResources = { ...columnData };
        visit.get('resources').forEach(resource => {
            const resourceName = resource.get('name');
            if (VisitTableUtils.staticResources.indexOf(resourceName) === -1) {
                columnDataWithResources[
                    resourceName
                ] = VisitUtils.getResourceValue(visit, resourceName);
            }
        });
        return columnDataWithResources;
    };

    static mapVisitToColumnForCustomFields: Function = function (
        visit,
        columnData: Object,
        customFields: ?Array<CustomField>
    ) {
        const modifiedColumns = { ...columnData };
        if (customFields) {
            customFields.forEach(customField => {
                const { key } = customField;
                modifiedColumns[key] = customField.getDisplayValue(
                    visit.getIn(['customFields', key])
                );
            });
        }

        return modifiedColumns;
    };

    static getTranslatedText: Function = function (key, keySuffix, lowerCase) {
        if (key && keySuffix) {
            return nsTranslate(
                `${key}.${lowerCase ? keySuffix.toLowerCase() : keySuffix}`
            );
        }
        return null;
    };
}
export default VisitTableUtils;
